import {
  BaseIcon,
  FormWarning,
  Modal,
  Popover,
} from "@shltelemedicine/shl-ui-lib";
import { ReactNode, useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import editIcon from "../../../../assets/shared/edit.svg";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../../hooks/useAuthorizedAction";
import AlertLevel from "../../../../models/alert";
import { KeyValue } from "../../../../models/key-value";
import {
  AddStudy,
  AddStudyFormInputs,
  IStudy,
  IVisit,
} from "../../../../models/study.interface";
import { getFormChangedData } from "../../../../utils/formUtil";
import AlertModal, {
  AlertModalProps,
} from "../../../Shared/AlertModal/AlertModal";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import StudyIcon from "../../../Shared/StudyIcon/StudyIcon";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import VisitsList from "../VisitsList/VisitsList";
import styles from "./AddStudyModal.module.css";

const noOfVisitsItems: KeyValue<string>[] = [];
for (let i = 0; i <= 10; i++) {
  noOfVisitsItems.push({
    key: i.toString(),
    value: i.toString(),
  });
}

type AddStudyModalProps = {
  show?: boolean;
  mode: "add" | "edit";
  study?: IStudy;
  onClose?: () => void;
  onSave?: (study: AddStudy | Partial<AddStudy>) => Promise<void>;
  editStudyName?: boolean;
};

const AddStudyModal = ({
  mode,
  show,
  study,
  onClose,
  onSave,
  editStudyName,
}: AddStudyModalProps) => {
  const formMethods = useForm<AddStudyFormInputs>();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors: formErrors, isDirty, dirtyFields },
    reset,
  } = formMethods;

  const intl = useIntl();
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  useEffect(() => {
    if (show === true) {
      reset(
        isEdit
          ? {
              name: study?.name,
              visits: study?.visits?.map((v) => ({
                name: v.name,
                hasOrders: v.hasOrders,
              })),
              reference: study?.reference,
            }
          : {}
      );
    }
  }, [show]); // eslint-disable-line

  const saveHandler = async (addStudyForm: AddStudyFormInputs) => {
    if (isEdit) {
      setShowEditConfirmModal(true);
      return;
    }

    await onSaveConfirmed(addStudyForm);
  };

  const onSaveConfirmed = async (addStudyForm: AddStudyFormInputs) => {
    if (addStudyForm) {
      addStudyForm.visits = addStudyForm.visits?.filter((item) => !!item) ?? [];
    }

    if (!onSave) return;
    setSaving(true);

    let dataToSave = !isEdit
      ? addStudyForm
      : getFormChangedData<Omit<AddStudyFormInputs, "visits">>(
          dirtyFields,
          getValues
        );

    // TODO: prevent this type casting
    (dataToSave as AddStudyFormInputs).visits = (dataToSave as AddStudyFormInputs).visits?.map(
      (visit) => {
        const { hasOrders, ...rest } = visit as IVisit;
        return rest;
      }
    );

    await onSave(dataToSave);
    setSaving(false);
  };

  const nameRequiredMsg = intl.messages[
    "MANAGEMENT.ADD_ENTITY.NAME_REQUIRED"
  ] as string;

  return (
    <>
      <Modal
        dialogClassName={styles.dialog}
        centered
        backdrop="static"
        show={show}
        onHide={onClose}
        header={
          <div className="d-flex align-items-center">
            {isEdit ? (
              <BaseIcon icon={editIcon} className={styles.editIcon} />
            ) : (
              <StudyIcon />
            )}

            <span className="mx-1"></span>
            <p className={`${styles.title} mb-0`}>
              <FormattedMessage
                id={
                  isEdit
                    ? "MANAGEMENT.STUDIES.EDIT_STUDY"
                    : "MANAGEMENT.STUDIES.ADD_STUDY"
                }
                defaultMessage={isEdit ? "Edit Study" : "Add Study"}
              />
            </p>
          </div>
        }
        footer={
          <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-sm-0">
            <CancelButton
              onClick={onClose}
              className="mx-2"
              disabled={saving}
            />

            <SaveButton
              onClick={handleSubmit(saveHandler)}
              className="prim-bg-gradient-color capitalized"
              disabled={saving || !isDirty}
            >
              <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
              {saving && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </SaveButton>
          </div>
        }
      >
        <Form className={`p-0 ${styles.form} h-100`}>
          <Container fluid className="px-4 mb-3">
            <Form.Row>
              <Col>
                {/* Study Name */}
                <Form.Label className="mb-1 app-form-label d-flex align-items-center">
                  <span className="prim-color align-middle">*</span>&nbsp;
                  <span>
                    <FormattedMessage
                      id="MANAGEMENT.STUDIES.ADD_STUDY.NAME"
                      defaultMessage="Study Name"
                    />
                  </span>
                  {formErrors?.name && (
                    <>
                      <span className="mx-1"></span>
                      <div className="flex-grow-1">
                        <FormWarning
                          className={`font-sm ${styles.warning}`}
                          message={nameRequiredMsg}
                        />
                      </div>
                    </>
                  )}
                </Form.Label>
                {!editStudyName || !isEdit ? (
                  <TextFormInput<AddStudyFormInputs>
                    name={"name"}
                    control={control}
                    rules={{
                      required: true,
                    }}
                    input={{
                      isInvalid: !!formErrors?.name,
                      disabled: saving,
                    }}
                  />
                ) : (
                  <Popover
                    id={uuidv4()}
                    hoverContent={
                      <PopoverContent
                        messageContent={
                          <FormattedMessage
                            id="MANAGEMENT.STUDIES.EDIT_STUDY_NAME.POPUP_MESSAGE"
                            defaultMessage="This field cannot be edited.<br></br>The study has ecgs assigned to it."
                            values={{
                              br: () => <br></br>,
                            }}
                          />
                        }
                      />
                    }
                  >
                    <span>
                      <TextFormInput<AddStudyFormInputs>
                        name={"name"}
                        control={control}
                        rules={{
                          required: true,
                        }}
                        input={{
                          isInvalid: !!formErrors?.name,
                          disabled: saving || editStudyName,
                          readOnly: true,
                        }}
                      />
                    </span>
                  </Popover>
                )}
              </Col>
              <Col>
                {/* Reference */}
                <Form.Label className="mb-1 app-form-label d-flex align-items-center">
                  <span>
                    <FormattedMessage
                      id="USER.REFERENCE"
                      defaultMessage="Reference"
                    />
                  </span>
                </Form.Label>
                <TextFormInput<AddStudyFormInputs>
                  name={"reference"}
                  control={control}
                  input={{
                    isInvalid: !!formErrors?.reference,
                    disabled: saving,
                  }}
                />
              </Col>
            </Form.Row>
          </Container>
          {!isCroExternOrg ? (
            <div className={styles.visitsListWrapper}>
              <FormProvider {...formMethods}>
                <VisitsList />
              </FormProvider>
            </div>
          ) : null}
        </Form>
      </Modal>
      {showEditConfirmModal && (
        <EditConfirmationModal
          dialog
          show={showEditConfirmModal}
          okBtn={{
            onClick: () => {
              setShowEditConfirmModal(false);
              onSaveConfirmed(getValues());
            },
            text: <FormattedMessage id="GLOBAL.YES" defaultMessage="Yes" />,
          }}
          cancelBtn={{
            text: <FormattedMessage id="GLOBAL.NO" defaultMessage="No" />,
          }}
          onClose={() => setShowEditConfirmModal(false)}
          level={AlertLevel.Warning}
        />
      )}
    </>
  );
};

export default AddStudyModal;

const EditConfirmationModal = (props: AlertModalProps) => {
  return (
    <AlertModal {...props}>
      <FormattedMessage
        id="MANAGEMENT.STUDIES.EDIT_STUDY.CONFIRMATION"
        defaultMessage="Are you sure you want to update the properties of this study?"
      />
    </AlertModal>
  );
};

type PopoverContentProps = {
  messageContent: ReactNode;
};

export const PopoverContent = ({ messageContent }: PopoverContentProps) => (
  <div style={{ maxWidth: "200px" }}>
    <p className="mb-0">
      <strong>
        <FormattedMessage
          id="MANAGEMENT.STUDIES.EDIT_STUDY.POPUP_TITLE"
          defaultMessage="Note"
        />
      </strong>
    </p>
    <p className="mb-0">{messageContent}</p>
  </div>
);
